import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import ReactModal from 'react-modal';

import { DbKey } from '../../../../adl-gen/common/db';
import { AppUser } from '../../../../adl-gen/totalinteraction/futurefit/db';
import { UsersGroupsMap } from '../../../../adl-gen/totalinteraction/futurefit/frontend';
import { i18next } from '../../../../internationalization/i18next';
import { getDefaultSelectedResults, SelectedResults } from '../../../../stores/manager-quiz-results-store';
import { CloseIcon, FilterSVGIcon } from '../../../common/icons';
import { Button } from '../../../widgets/button/button';
// import { assertNever } from "../../../util/types";
import { Item, SearchableCheckItemSet } from '../../../widgets/searchable-check-item-set/searchable-check-item-set';
import {
  FieldState,
  FormValue,
  InputState,
  newTextField,
  withValueCallback
} from '../../../widgets/text-input/text-field';
import { TextInput } from '../../../widgets/text-input/text-input';

ReactModal.setAppElement('#root');

const styles = require('./user-groups-filter.css');

interface UserGroupsFilterProps {
  initial: SelectedResults;
  usersGroupsMap: UsersGroupsMap;
  usersCompleted: DbKey<AppUser>[];

  onCancel(): void;
  onDone(selected: SelectedResults): void;
}

interface Toggle {
  label: string;
  detail: string;
}

@observer
export class UserGroupsFilter extends React.Component<UserGroupsFilterProps> {
  @observable
  searchState: InputState;

  // store the users/groups independently to maintain switching tabs
  @observable
  listShowing: 'users' | 'groups' = this.props.initial.kind;
  @observable
  usersSelected: string[] = this.props.initial.kind === 'users' ? this.props.initial.users : this.props.usersCompleted;
  @observable
  groupsSelected: string[] =
    this.props.initial.kind === 'groups'
      ? this.props.initial.groups
      : Object.keys(this.props.usersGroupsMap.userGroups);

  constructor(props: Readonly<UserGroupsFilterProps>) {
    super(props);
    this.searchState = textFieldState(this.onSearchResult);
  }

  render() {
    return (
      <ReactModal
        isOpen={true}
        shouldCloseOnEsc={true}
        shouldCloseOnOverlayClick={true}
        onRequestClose={this.props.onCancel}
        className={styles.modal}
      >
        {this.renderCloseButton()}
        {this.renderTitle()}
        {this.renderFilterCategorySelector()}
        {this.renderSearch()}
        {this.renderCheckItems()}
        <br />
        {this.renderButtons()}
      </ReactModal>
    );
  }

  renderCloseButton(): JSX.Element {
    return (
      <div className={styles.closeButton}>
        <CloseIcon onClick={this.props.onCancel} size={'xsmall'} />
      </div>
    );
  }

  renderTitle(): JSX.Element {
    return (
      <div className={styles.title}>
        <FilterSVGIcon className={styles.filterIcon} size={'small'} />
        <div className={styles.titleText}>{i18next.t('filters.people.title', 'Filters')}</div>
      </div>
    );
  }

  renderFilterCategorySelector(): JSX.Element {
    return (
      <div className={styles.filterCategorySelector}>
        <div className={styles.showing}>{i18next.t('legend.showing', 'Showing')} </div>
        <select
          value={kindToLabel(this.listShowing)}
          onChange={e => this.onSwitchList(e.target.value as 'Users' | 'Groups')}
          className={styles.select}
        >
          <option value={'Users'}>{i18next.t('filters.people.filter-people', 'My People')}</option>
          <option value={'Groups'}>{i18next.t('filters.people.filter-groups', 'My Groups')}</option>
        </select>
      </div>
    );
  }

  renderSearch(): JSX.Element {
    return (
      <div className={styles.search}>
        <TextInput placeholder={i18next.t('filters.placeholder', 'Search')} state={this.searchState} type={'text'} />
      </div>
    );
  }

  // Sort by surname and forename
  sortBySurname = (a: string, b: string) => {
    const surnameA = a.split(' ', 2).length > 1;
    const surnameB = b.split(' ', 2).length > 1;
    if (surnameA && surnameB) {
      return (
        a.split(' ', 2)[1].localeCompare(b.split(' ', 2)[1]) || a.split(' ', 2)[0].localeCompare(b.split(' ', 2)[0])
      );
    } else if (surnameA) {
      return a.split(' ', 2)[1].localeCompare(b) || a.split(' ', 2)[0].localeCompare(b);
    } else if (surnameB) {
      return a.localeCompare(b.split(' ', 2)[1]) || a.localeCompare(b.split(' ', 2)[0]);
    } else {
      return a.localeCompare(b);
    }
  };

  renderCheckItems(): JSX.Element {
    /// Case-insensitive search - first clean up the expression so it's a valid regex
    const term = this.searchState.value().replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&');
    const re = new RegExp(term, 'i');
    if (this.current.kind === 'users') {
      this.items.sort((a, b) => this.sortBySurname(a.value.label.trim(), b.value.label.trim()));
    } else {
      this.items.sort((a, b) => a.value.label.localeCompare(b.value.label));
    }
    return (
      <div className={styles.checkItems}>
        <SearchableCheckItemSet
          items={this.items.filter(i => i.value.label.search(re) !== -1)}
          hintText={t => t.detail}
          label={t => t.label}
          onChange={this.onChange}
          selectAllVisible={true}
        />
      </div>
    );
  }

  renderButtons(): JSX.Element {
    return (
      <div className={styles.buttons}>
        <Button
          // className={classnames(styles.button, styles.clear)}
          variant={'tertiary'}
          onClick={async () => this.onClear()}
        >
          {i18next.t('filters.clear', 'Clear Filter')}
        </Button>
        <Button
          // className={classnames(styles.button, styles.report)}
          onClick={async () => this.props.onDone(this.current)}
          disabled={this.requeryDisabled()}
        >
          {this.buttonText()}
        </Button>
      </div>
    );
  }

  @action
  onClear(): void {
    const defaultSelection = getDefaultSelectedResults(this.props.usersCompleted);
    this.listShowing = defaultSelection.kind;
    this.usersSelected = this.props.usersCompleted;
    this.groupsSelected = Object.keys(this.props.usersGroupsMap.userGroups);
  }

  requeryDisabled(): boolean {
    if (this.listShowing === 'users') {
      return this.usersSelected.length === 0;
    } else if (this.listShowing === 'groups') {
      return this.groupsSelected.length === 0;
    }
    throw Error('Unrecognised list');
  }

  buttonText(): string {
    if (this.listShowing === 'users') {
      return i18next.t('filters.people.users', {
        count: this.usersSelected.length,
        defaultValue: `Report on ${this.usersSelected.length} users`
      });
    } else if (this.listShowing === 'groups') {
      return i18next.t('filters.people.groups', {
        count: this.groupsSelected.length,
        defaultValue: `Report on ${this.groupsSelected.length} groups`
      });
    }
    throw Error('Unrecognised list');
  }

  @action
  onSwitchList = (list: 'Users' | 'Groups') => {
    this.listShowing = labelToKind(list);
  };

  @action
  onChange = (onChangeId: string): void => {
    const exists = this.selectedIds.find(id => onChangeId === id);
    this.selectedIds = exists
      ? this.selectedIds.filter(userId => onChangeId !== userId)
      : this.selectedIds.concat(onChangeId);
  };

  /// ----- [ FF2-439 ] ---------------------------------------------------
  /// The Search field does not consistently trigger relevant onChange
  /// handlers to manage the list filtered; it works fine if "Select All"
  /// is cleared before text entry, but does not correctly filter if "Select All"
  /// is checked.
  ///
  /// onChange runs a toggle, which doesn't work if fired consequent
  /// to a change in searchState state. In this case, we want to
  /// explicitly set the items in selectedIds.
  @action
  onSearchResult = (searchTerm: FormValue<string>): void => {
    /// Case-insensitive search - first clean up the expression so it's a valid regex
    /// FF2-488 BUGFIX switch this off if search term value = "", otherwise manual selections
    /// by checkbox are ignored.
    if (searchTerm.kind === 'valid' && searchTerm.value !== '') {
      const term = searchTerm.value.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&');
      const re = new RegExp(term, 'i');
      this.selectedIds = this.items
        .reduce((acc: Item<Toggle>[], i) => {
          /// ----- [ FF2-439 ] ---------------------------------------------------
          /// Side-effect: This code will always "select all", even if the items are not
          /// already selected. But I think that is what is intended - ie. if a
          /// search term is entered, assume the user wants to include all the results
          if (i.value.label.search(re) !== -1 || searchTerm.value === '') {
            acc.push(i);
          }
          return acc;
        }, [])
        .map(i => i.id);
    }
  };

  @computed
  get current(): SelectedResults {
    if (this.listShowing === 'users') {
      return {
        kind: 'users',
        users: this.usersSelected
      };
    } else if (this.listShowing === 'groups') {
      return {
        kind: 'groups',
        groups: this.groupsSelected
      };
    }
    throw Error('Unrecognised list');
  }

  @computed
  get selectedIds(): string[] {
    if (this.current.kind === 'users') {
      return this.current.users;
    } else if (this.current.kind === 'groups') {
      return this.current.groups;
    }
    throw Error('Unrecognised list');
  }

  set selectedIds(ids: string[]) {
    if (this.current.kind === 'users') {
      this.usersSelected = ids;
    } else if (this.current.kind === 'groups') {
      this.groupsSelected = ids;
    }
  }

  @computed
  get allIds(): string[] {
    if (this.current.kind === 'users') {
      return this.props.usersCompleted;
    } else if (this.current.kind === 'groups') {
      return Object.keys(this.props.usersGroupsMap.userGroups);
    }
    throw Error('Unrecognised list');
  }

  @computed
  get items(): Item<Toggle>[] {
    return this.allIds.map(userId => this.idToItem(this.current.kind, userId, this.selectedIds));
  }

  idToItem(kind: 'users' | 'groups', id: string, selected: string[]): Item<Toggle> {
    return {
      id,
      selected: selected.includes(id),
      value: kind === 'users' ? this.userIdToToggle(id) : this.groupIdToToggle(id)
    };
  }

  userIdToToggle(userId: string) {
    return {
      label: this.props.usersGroupsMap.appUsers[userId].fullname,
      detail: this.props.usersGroupsMap.appUsers[userId].email
    };
  }

  groupIdToToggle(groupId: string) {
    return {
      label: this.props.usersGroupsMap.userGroups[groupId].name,
      detail: this.props.usersGroupsMap.userGroups[groupId].name
    };
  }
}

function kindToLabel(kind: 'users' | 'groups'): string {
  if (kind === 'users') {
    return 'Users';
  } else if (kind === 'groups') {
    return 'Groups';
  }
  throw Error('Unrecognised kind');
}

function labelToKind(label: 'Users' | 'Groups') {
  if (label === 'Users') {
    return 'users';
  } else if (label === 'Groups') {
    return 'groups';
  }
  throw Error('Unrecognised label');
}

/// ----- [ FF2-439 ] ---------------------------------------------------
/// Graft a change callback action on the searchState field
function textFieldState(searchResultFn: (searchTerm: FormValue<string>) => void, initialValue: string = '') {
  const state: FieldState<string> = withValueCallback(newTextField(), searchResultFn);
  state.onChange(initialValue);
  return state;
}



// WEBPACK FOOTER //
// ./src/ui/components/filters/user-groups-filter/user-groups-filter.tsx